import { useMemo, useRef, useState } from 'react';
import { Button } from './Button/Button';
import { Modal } from './Modal/Modal';
import { SegmentedToggle } from './SegmentedToggle/SegmentedToggle';
import styles from './ImportModal.module.css';
import { parseFasteBudgetCsv } from '../features/import/parseFasteBudgetCsv';
import { detectKindFromFilename } from '../features/import/detectKind';
import { ParseError, type ParseResult } from '../features/import/types';
import {
  parseBankStatement,
  suggestBankMapping,
  type BankPayeeGroup,
} from '../features/import/parseBankStatement';
import {
  useMe,
  usePayeeMappings,
  useUpsertPayeeMapping,
  useYearBundle,
  useImportYear,
  useUpdateItem,
} from '../store/queryHooks';
import { useUiStore } from '../store/ui';
import type { CategoryKind, ImportPayload } from '@budgetx/shared';

/**
 * Read a CSV file that may be UTF-8 or Windows-1252 (the common Danish
 * bank export encoding). Uses file.text() (UTF-8) as the primary path;
 * if the result contains replacement chars (U+FFFD) we re-read the raw
 * bytes and decode as Windows-1252.
 */
async function readCsvText(file: File): Promise<string> {
  const utf8 = await file.text();
  if (!utf8.includes('�')) return utf8;
  try {
    const buf = await file.arrayBuffer();
    return new TextDecoder('windows-1252').decode(buf);
  } catch {
    return utf8;
  }
}

interface Props {
  onClose(): void;
}

type Tab = 'budget' | 'bank';
type ExposedKind = 'income' | 'fast' | 'variabel';
type Step = 'file' | 'review' | 'confirm';

const KIND_LABEL: Record<ExposedKind, string> = {
  income: 'Indtægter',
  fast: 'Faste udgifter',
  variabel: 'Variable udgifter',
};

const KIND_LABEL_LOWER: Record<ExposedKind, string> = {
  income: 'indtægter',
  fast: 'faste udgifter',
  variabel: 'variable udgifter',
};

const KIND_OPTIONS = [
  { value: 'income', label: 'Indtægter' },
  { value: 'fast', label: 'Faste' },
  { value: 'variabel', label: 'Variable' },
] as const;

const TAB_OPTIONS = [
  { value: 'budget', label: 'Budget-CSV' },
  { value: 'bank', label: 'Kontoudtræk (bank)' },
] as const;

export function ImportModal({ onClose }: Props) {
  const [tab, setTab] = useState<Tab>('budget');
  if (tab === 'bank') {
    return <BankImportTab tab={tab} setTab={setTab} onClose={onClose} />;
  }
  return <BudgetImportTab tab={tab} setTab={setTab} onClose={onClose} />;
}

interface TabProps {
  tab: Tab;
  setTab(t: Tab): void;
  onClose(): void;
}

function TabSwitcher({ tab, setTab }: { tab: Tab; setTab(t: Tab): void }) {
  return (
    <div className={styles.tabSwitcher}>
      <SegmentedToggle options={TAB_OPTIONS} value={tab} onChange={setTab} />
    </div>
  );
}

// ---- Budget-CSV tab (existing flow) ----------------------------------

function BudgetImportTab({ tab, setTab, onClose }: TabProps) {
  const [step, setStep] = useState<Step>('file');
  const [file, setFile] = useState<File | null>(null);
  const [parsed, setParsed] = useState<ParseResult | null>(null);
  const [parseErr, setParseErr] = useState<string | null>(null);
  const [targetYear, setTargetYear] = useState<number | null>(null);
  const [importKind, setImportKind] = useState<ExposedKind>('fast');
  const inputRef = useRef<HTMLInputElement>(null);

  async function onFile(f: File) {
    setParseErr(null);
    try {
      const text = await readCsvText(f);
      const result = parseFasteBudgetCsv(text);
      setFile(f);
      setParsed(result);
      const detected = detectKindFromFilename(f.name);
      setImportKind(detected === 'opsparing' ? 'fast' : (detected as ExposedKind));
      setStep('review');
    } catch (err) {
      const msg =
        err instanceof ParseError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'ukendt fejl';
      setParseErr(`Kunne ikke læse CSV-filen: ${msg}`);
    }
  }

  if (step === 'file') {
    return (
      <Modal
        title="Importér"
        subtitle="Vælg hvad du vil importere."
        onClose={onClose}
        actions={
          <Button variant="secondary" onClick={onClose}>
            Annullér
          </Button>
        }
      >
        <TabSwitcher tab={tab} setTab={setTab} />
        <div className={styles.tabHelp}>Budget-CSV for indtægter, faste eller variable udgifter. Type genkendes ud fra filnavnet.</div>
        <div
          className={styles.drop}
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            const f = e.dataTransfer.files[0];
            if (f) void onFile(f);
          }}
          role="button"
          tabIndex={0}
        >
          <div className={styles.arrow}>↑</div>
          <div className={styles.title}>Træk din CSV-fil hertil</div>
          <div className={styles.hint}>eller klik for at vælge</div>
          <input
            ref={inputRef}
            type="file"
            accept=".csv,text/csv"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void onFile(f);
            }}
          />
        </div>
        {parseErr && (
          <div className={styles.error} role="alert">
            {parseErr}
          </div>
        )}
      </Modal>
    );
  }

  if (step === 'review' && parsed && file) {
    return (
      <ReviewStep
        file={file}
        parsed={parsed}
        kind={importKind}
        setKind={setImportKind}
        onCancel={onClose}
        onBack={() => setStep('file')}
        onContinue={(year) => {
          setTargetYear(year);
          setStep('confirm');
        }}
      />
    );
  }

  if (step === 'confirm' && parsed && targetYear !== null) {
    return (
      <ConfirmStep
        year={targetYear}
        kind={importKind}
        parsed={parsed}
        onCancel={onClose}
        onBack={() => setStep('review')}
        onDone={onClose}
      />
    );
  }

  return null;
}

interface ReviewProps {
  file: File;
  parsed: ParseResult;
  kind: ExposedKind;
  setKind(k: ExposedKind): void;
  onCancel(): void;
  onBack(): void;
  onContinue(year: number): void;
}

function ReviewStep({ file, parsed, kind, setKind, onCancel, onBack, onContinue }: ReviewProps) {
  const [year, setYear] = useState<number>(parsed.csvYear);
  const [showWarnings, setShowWarnings] = useState(false);
  const bundle = useYearBundle(year, Number.isFinite(year) && year >= 2000 && year <= 2100);

  const totalItems = useMemo(
    () => parsed.categories.reduce((s, c) => s + c.items.length, 0),
    [parsed],
  );

  const existingForKind = bundle.data?.categories.filter((c) => c.kind === kind) ?? [];
  const existingCats = existingForKind.length;
  const existingItems = existingForKind.reduce((s, c) => s + c.items.length, 0);

  const yearState: 'loading' | 'empty' | 'has-data' | 'not-found' | 'invalid' =
    !Number.isFinite(year) || year < 2000 || year > 2100
      ? 'invalid'
      : bundle.isLoading
        ? 'loading'
        : bundle.error
          ? 'not-found'
          : existingCats === 0
            ? 'empty'
            : 'has-data';

  return (
    <Modal
      title="Importér"
      subtitle={file.name}
      onClose={onCancel}
      actions={
        <>
          <Button variant="secondary" onClick={onBack}>
            Tilbage
          </Button>
          <Button
            variant="primary"
            onClick={() => onContinue(year)}
            disabled={yearState === 'invalid' || yearState === 'loading'}
          >
            Fortsæt
          </Button>
        </>
      }
    >
      <div className={styles.summary}>
        <div className={styles.summaryFile}>{file.name}</div>
        <div className={styles.summaryMeta}>
          {parsed.categories.length} kategorier · {totalItems} posteringer · CSV-år {parsed.csvYear}
        </div>
      </div>

      <div className={styles.field}>
        <label className={styles.label}>Type</label>
        <SegmentedToggle options={KIND_OPTIONS} value={kind} onChange={setKind} />
      </div>

      <div className={styles.field}>
        <label className={styles.label} htmlFor="import-year">
          Målår
        </label>
        <input
          id="import-year"
          className={styles.input}
          type="number"
          min={2000}
          max={2100}
          value={Number.isFinite(year) ? year : ''}
          onChange={(e) => setYear(Number(e.target.value))}
        />
      </div>

      {yearState === 'empty' && (
        <div className={styles.info}>
          År {year} har ingen {KIND_LABEL_LOWER[kind]} — importen tilføjer {totalItems} poster.
        </div>
      )}
      {yearState === 'not-found' && (
        <div className={styles.info}>År {year} findes ikke endnu — det bliver oprettet.</div>
      )}
      {yearState === 'has-data' && (
        <div className={styles.warn}>
          ⚠ År {year} indeholder allerede {existingCats} kategorier og {existingItems} posteringer i
          "{KIND_LABEL[kind]}". De bliver slettet.
        </div>
      )}

      {parsed.warnings.length > 0 && (
        <div className={styles.warnings}>
          <button
            type="button"
            className={styles.warningsToggle}
            onClick={() => setShowWarnings((v) => !v)}
          >
            {showWarnings ? 'Skjul' : 'Vis'} advarsler ({parsed.warnings.length})
          </button>
          {showWarnings && (
            <ul className={styles.warningsList}>
              {parsed.warnings.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Modal>
  );
}

interface ConfirmProps {
  year: number;
  kind: ExposedKind;
  parsed: ParseResult;
  onCancel(): void;
  onBack(): void;
  onDone(): void;
}

function ConfirmStep({ year, kind, parsed, onCancel, onBack, onDone }: ConfirmProps) {
  const [typed, setTyped] = useState('');
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const importMut = useImportYear();
  const setToast = useUiStore((s) => s.setToast);

  const totalItems = parsed.categories.reduce((s, c) => s + c.items.length, 0);
  const confirmed = typed === String(year);

  async function doImport() {
    setErrMsg(null);
    const payload: ImportPayload = {
      kind: kind as CategoryKind,
      categories: parsed.categories.map((c) => ({
        name: c.name,
        items: c.items.map((it) => ({
          name: it.name,
          budgets: it.budgets,
          actuals: it.actuals,
        })),
      })),
    };
    try {
      await importMut.mutateAsync({ year, kind: kind as CategoryKind, payload });
      setToast(
        `Importeret ${parsed.categories.length} kategorier og ${totalItems} posteringer i "${KIND_LABEL[kind]}" for ${year}`,
      );
      onDone();
    } catch (err) {
      setErrMsg(err instanceof Error ? err.message : 'Ukendt fejl under import');
    }
  }

  return (
    <Modal
      title="Bekræft import"
      subtitle="Denne handling kan ikke fortrydes."
      onClose={onCancel}
      actions={
        <>
          <Button variant="secondary" onClick={onBack} disabled={importMut.isPending}>
            Tilbage
          </Button>
          <Button
            variant="primary"
            className={styles.dangerBtn}
            disabled={!confirmed || importMut.isPending}
            onClick={() => void doImport()}
          >
            {importMut.isPending ? 'Importerer…' : 'Slet og importér'}
          </Button>
        </>
      }
    >
      <div className={styles.yearBig}>{year}</div>
      <div className={styles.dangerConfirm}>
        Denne handling sletter alle nuværende {KIND_LABEL_LOWER[kind]} for {year} og kan ikke
        fortrydes.
      </div>
      <div className={styles.field}>
        <label className={styles.label} htmlFor="import-confirm">
          Skriv "{year}" for at bekræfte
        </label>
        <input
          id="import-confirm"
          className={styles.input}
          type="text"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          autoFocus
        />
      </div>
      {errMsg && (
        <div className={styles.error} role="alert">
          {errMsg}
        </div>
      )}
    </Modal>
  );
}

// ---- Bank statement tab (new in v9) ----------------------------------

function BankImportTab({ tab, setTab, onClose }: TabProps) {
  const year = useUiStore((s) => s.year);
  const setToast = useUiStore((s) => s.setToast);
  const bundle = useYearBundle(year);
  const me = useMe();
  const budgetStartDay = me.data?.budget_start_day ?? 1;
  const updateItem = useUpdateItem(year);
  const savedMappings = usePayeeMappings();
  const upsertMapping = useUpsertPayeeMapping();
  const [fileName, setFileName] = useState<string | null>(null);
  const [groups, setGroups] = useState<BankPayeeGroup[] | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [parseErr, setParseErr] = useState<string | null>(null);
  const [skippedYearCount, setSkippedYearCount] = useState(0);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Persistent payee → itemKey lookup ("categoryId:itemId" strings).
  const persistentMapByPayee = useMemo(() => {
    const out = new Map<string, string>();
    if (!savedMappings.data || !bundle.data) return out;
    for (const m of savedMappings.data) {
      // Find the fast category containing this item so we can build the composite key.
      for (const c of bundle.data.categories) {
        if (c.kind !== 'fast') continue;
        if (c.items.some((it) => it.id === m.item_id)) {
          out.set(m.payee_normalized, `${c.id}:${m.item_id}`);
          break;
        }
      }
    }
    return out;
  }, [savedMappings.data, bundle.data]);

  // Flat list of fast items: "categoryId:itemId" -> "Category › Item"
  const fastItems = useMemo(() => {
    if (!bundle.data) return [];
    return bundle.data.categories
      .filter((c) => c.kind === 'fast')
      .flatMap((c) =>
        c.items.map((it) => ({
          key: `${c.id}:${it.id}`,
          categoryName: c.name,
          itemName: it.name,
        })),
      );
  }, [bundle.data]);

  async function onFile(f: File) {
    setParseErr(null);
    try {
      const text = await readCsvText(f);
      const res = parseBankStatement(text, { budgetStartDay, targetYear: year });
      setFileName(f.name);
      setGroups(res.groups);
      setSkippedYearCount(res.skippedYearCount);
      // Persistent mappings win; fall back to fuzzy suggestion for unknown payees.
      const fuzzy = suggestBankMapping(res.groups, fastItems);
      const merged: Record<string, string> = {};
      for (const g of res.groups) {
        merged[g.id] = persistentMapByPayee.get(g.id) ?? fuzzy[g.id] ?? '';
      }
      setMapping(merged);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'ukendt fejl';
      setParseErr(msg);
      setGroups(null);
      setMapping({});
      setFileName(f.name);
    }
  }

  const mappedCount = Object.values(mapping).filter((v) => v).length;

  async function confirm() {
    if (!groups) return;
    setBusy(true);
    try {
      // Aggregate {itemKey: {month: totalAmount}}
      const perItem: Record<string, Record<number, number>> = {};
      for (const g of groups) {
        const target = mapping[g.id];
        if (!target) continue;
        perItem[target] ??= {};
        for (const [m, v] of Object.entries(g.months)) {
          perItem[target][+m] = (perItem[target][+m] ?? 0) + v;
        }
      }
      if (Object.keys(perItem).length === 0) {
        setToast('Vælg mindst én kobling først');
        setBusy(false);
        return;
      }

      // For each mapped item, merge its months into the existing actuals array.
      for (const [key, months] of Object.entries(perItem)) {
        const [, itemId] = key.split(':').map(Number);
        if (!itemId) continue;
        // Find current actuals for this item to merge, not overwrite.
        let currentActuals: (number | null)[] = Array(12).fill(null);
        for (const c of bundle.data?.categories ?? []) {
          const it = c.items.find((x) => x.id === itemId);
          if (it) {
            currentActuals = it.actuals.slice();
            break;
          }
        }
        for (const [m, v] of Object.entries(months)) {
          currentActuals[+m] = Math.round(v);
        }
        await updateItem.mutateAsync({ id: itemId, actuals: currentActuals });
      }

      // Persist all non-empty mappings so next import pre-fills them.
      const mappingSaves: Promise<unknown>[] = [];
      for (const g of groups) {
        const target = mapping[g.id];
        if (!target) continue;
        const [, itemId] = target.split(':').map(Number);
        if (!itemId) continue;
        // Only save if this differs from what's already persisted (avoid noise).
        if (persistentMapByPayee.get(g.id) === target) continue;
        mappingSaves.push(
          upsertMapping.mutateAsync({ payee_normalized: g.id, item_id: itemId }),
        );
      }
      await Promise.all(mappingSaves);

      const savedCount = mappingSaves.length;
      setToast(
        `Faktiske faste udgifter opdateret fra kontoudtræk (${Object.keys(perItem).length} posteringer)` +
          (savedCount > 0 ? ` · ${savedCount} nye mappings gemt` : ''),
      );
      onClose();
    } catch (err) {
      setToast(err instanceof Error ? err.message : 'Kunne ikke opdatere');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Importér"
      subtitle={`Importér til ${year}.`}
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Annullér
          </Button>
          <Button
            variant="primary"
            onClick={() => void confirm()}
            disabled={!groups || mappedCount === 0 || busy}
          >
            {busy ? 'Opdaterer…' : `Udfyld faste udgifter (${mappedCount})`}
          </Button>
        </>
      }
    >
      <TabSwitcher tab={tab} setTab={setTab} />
      <div className={styles.tabHelp}>
        Indlæs et kontoudtræk fra banken (CSV med dato, tekst og beløb). Udgifter grupperes pr.
        betalingsmodtager, som du kobler til dine faste udgifter. De faktiske beløb udfyldes ud
        fra filen.
        {budgetStartDay > 1 && (
          <>
            {' '}
            <strong>Månedens startdag er sat til {budgetStartDay}</strong> — posteringer før den
            dato tilhører den forrige måned.
          </>
        )}
      </div>

      <div
        className={styles.drop}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          const f = e.dataTransfer.files[0];
          if (f) void onFile(f);
        }}
        role="button"
        tabIndex={0}
      >
        <div className={styles.arrow}>↑</div>
        <div className={styles.title}>Træk dit kontoudtræk hertil</div>
        <div className={styles.hint}>eller klik for at vælge</div>
        <input
          ref={inputRef}
          type="file"
          accept=".csv,text/csv"
          style={{ display: 'none' }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void onFile(f);
          }}
        />
      </div>

      {parseErr && (
        <div className={styles.error} role="alert">
          <strong>{fileName ?? 'Fil'}</strong>: {parseErr}
        </div>
      )}

      {skippedYearCount > 0 && (
        <div className={styles.tabHelp}>
          <strong>{skippedYearCount}</strong> posteringer sprunget over — de tilhører et andet
          år end {year} og er ikke medtaget.
        </div>
      )}

      {groups && groups.length > 0 && (
        <>
          <div className={styles.bankGroupsLabel}>Kobl betalingsmodtagere til faste udgifter</div>
          <div className={styles.bankGroups}>
            {groups.map((g) => (
              <div key={g.id} className={styles.bankGroup}>
                <div className={styles.bankGroupHead}>
                  <div className={styles.bankGroupText}>
                    <div className={styles.bankGroupLabel}>{g.label}</div>
                    <div className={styles.bankGroupCount}>{g.count} posteringer</div>
                  </div>
                  <div className={styles.bankGroupTotal}>
                    {Math.round(g.total).toLocaleString('da-DK')} kr.
                  </div>
                </div>
                <select
                  className={styles.bankSelect}
                  value={mapping[g.id] ?? ''}
                  onChange={(e) =>
                    setMapping((prev) => ({ ...prev, [g.id]: e.target.value }))
                  }
                >
                  <option value="">— Ignorér —</option>
                  {fastItems.map((it) => (
                    <option key={it.key} value={it.key}>
                      {it.categoryName} › {it.itemName}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        </>
      )}
    </Modal>
  );
}
